






















































































































































import { Component, Prop, Inject, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import ClientMixin from "@/mixins/clientMixin";

// Enums/Data
import { RegexMatch } from "@/data/enums/router";
import { QUERY_PARAMS, ListDisplayMode } from "@/data/constants";
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IBrand } from "@/models/brands";
import { IClient } from "@/models/clients";
import { IClientsProviderMethods } from "@/models/providers/client";
import { IInvoice } from "@/models/invoices";

@Component({
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.brand.id;
      }
    })
  },
  components: {
    "client-list-item": () =>
      import("@/components/app/admin/clients/clientListItem.vue"),
    "tags-cloud-view": () =>
      import("@/components/app/global/tags/tagsCloudView.vue")
  }
})
export default class ClientListItemSwitcher extends Mixins(ClientMixin) {
  // mapState
  public brandId!: IBrand["id"];

  // Inject - Clients provider methods

  @Inject({ from: "clientsProviderMethods" })
  public $clientsMethods!: IClientsProviderMethods;

  // Props

  @Prop({ default: null })
  public readonly client!: IClient;

  @Prop({ default: ListDisplayMode.GRID })
  public readonly displayMode!: ListDisplayMode;

  @Prop({ default: false })
  public readonly isLoading!: boolean;

  // Methods

  public openNewTicketModal() {
    this.$store.dispatch("data/tickets/openNewTicketModal", {
      clientId: this.clientId,
      accountId: this.accountId,
      brandId: this.client.brand_id
    });
  }

  public placeNewQuickOrder() {
    this.$store.dispatch(`data/${DataModules.BASKETS}/openQuickOrderModal`, {
      props: {
        clientId: this.clientId,
        accountId: this.accountId
      },
      events: {
        success: (invoice: IInvoice) => {
          this.$router
            .push(
              this.$store.getters[`data/${DataModules.INVOICES}/getRoute`](
                invoice.id,
                invoice.client_id
              )
            )
            .catch((this.$router as any).handleFailure);
        }
      }
    });
  }

  public impersonateClient() {
    if (!this.canImpersonateClient) return;
    return this.$store.dispatch("auth/admin/impersonateClient", this.clientId);
  }

  public impersonateUser() {
    if (!this.canImpersonateOrg) return Promise.resolve();
    return this.$store.dispatch("auth/admin/impersonateUser", {
      orgId: this.client.upmind_org?.id,
      userId: this.client.upmind_org_user_id
    });
  }

  public deleteClient() {
    if (!this.canDeleteClient) return;
    return this.$store.dispatch(`data/${DataModules.CLIENTS}/confirmDelete`, {
      clientId: this.clientId,
      onSuccess: this.$clientsMethods.reloadData
    });
  }

  public addTag() {
    this.$store.dispatch(`data/${DataModules.TAGS}/openSelectModal`, {
      canCancel: ["escape", "outside", "x", "button"],
      props: {
        brandId: this.brandId,
        activeTags: this.clientTags
      },
      events: {
        success: async tags => {
          await this.assignTags(this.$_.map(tags, tag => tag.id));
          this.$store.dispatch("toast/show", {
            message: this.$t("_sentence.tags_updated")
          });
        }
      }
    });
  }

  public async detachTag(tag) {
    if (!this.canManageTag) return;

    try {
      const tagIds = this.$_.map(
        this.$_.filter(this.clientTags, clientTag => clientTag.id !== tag.id),
        t => t.id
      );
      await this.assignTags(tagIds);
      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.tag_unassigned")
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async assignTags(tagIds) {
    const tags = this.clientTags.filter(tag => tagIds.includes(tag.id));

    await this.$store.dispatch(`data/${DataModules.CLIENTS}/assignTags`, {
      id: this.client.id,
      tagIds
    });

    this.$bus.$emit("admin/client/tags/refresh", {
      client: this.client,
      tags
    });
  }

  // Computed

  public get isGridView() {
    return this.displayMode === ListDisplayMode.GRID;
  }

  public get clientName() {
    return this.getClientName({
      client: this.client,
      clientId: this.client?.id,
      orEmail: false
    });
  }

  public get clientTags() {
    return this.client?.tags || [];
  }

  public get adminOrderRoute() {
    return {
      name: "adminOrderShop",
      params: { basketId: RegexMatch.NEW },
      query: {
        [QUERY_PARAMS.CLIENT_ID]: this.clientId,
        [QUERY_PARAMS.ACCOUNT_ID]: this.accountId
      }
    };
  }

  public get defaultEmail() {
    return this.client?.default_email?.email;
  }

  public get clientId() {
    return this.client?.id;
  }

  public get accountId() {
    return this.client?.accounts?.[0]?.id;
  }

  public get canManageTag() {
    return this.$userCan("attach_client_tag");
  }

  public get canDeleteClient(): boolean {
    return this.$userCan("delete_client");
  }

  public get canImpersonateOrg(): boolean {
    return this.$store.getters[`data/${DataModules.CLIENTS}/canImpersonateOrg`](
      this.client
    );
  }

  public get canImpersonateClient(): boolean {
    return this.$store.getters[
      `data/${DataModules.CLIENTS}/canImpersonateClient`
    ](this.client);
  }

  public get isStaged() {
    return !!this.client?.staged_import;
  }
}
